import React, { useState, useEffect, ReactElement, useMemo, memo } from 'react';
import styled from '@emotion/styled';
import { Slider, Switch } from 'swui';
import { ANNUAL_INCOME_OPTIONS, EDUCATION_OPTIONS, YES_NO_OPTIONS, NET_WORTH_OPTIONS } from '../../join/constants';
import JoinCheckboxFormGroup from '../../join/JoinCheckboxFormGroup';
import JoinOptionButtonGroup from '../../join/JoinOptionButtonGroup';
import JoinRadioButtonFormGroup from '../../join/JoinRadioButtonFormGroup';
import { DashlineSeparator } from '../../join/styledElements';
import TextInput from '../../../atoms/TextInput';
import { handleProfileCompletionVysionEvent } from '../../../utils/vysion/profileCompletion';
import { VYSION_ACTIONS } from '../../../utils/vysion';
import { ProfileCompletionClickVysionEventsEnum } from '../../../utils/vysion/profileCompletion/click';
import { HEIGHTS_VALUES, OCCUPATION_MAX_CHARACTER } from '../../../config/constants';
import { cmToFeetInches, convertCMToInchesHeight, useFilteredBodyType } from '../../../utils/helpers';
import { shouldShowHeignInFeet } from '../../../common';
import { getStatusColor } from '../../../style';
import { ICompleteProfileFormData } from '../types/CompleteProfileWall';
import UsernameInputV2 from '../../../atoms/UsernameInputV2';

const StyleSlider = styled('div')({
  marginTop: 0,
  span: {
    backgroundColor: '#D9D9D9 !important',
  },
});

const SliderValue = styled('span')({
  fontSize: 14,
  fontWeight: 600,
});

const Counter = styled('div')<{ hasError: boolean }>(
  {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '16px',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  ({ hasError }) => ({
    color: !hasError ? '#F6F4F4' : getStatusColor().error,
  })
);

interface ICompleteProfileDetailsFormProps {
  handleFormData: (state: Partial<ICompleteProfileFormData>) => void;
  formData: ICompleteProfileFormData;
  showRequiredProfileDetailLists: Record<string, boolean>;
  gender: string;
  country: string;
  occupation: string;
}

const customButtonGroupStyle = {
  display: 'flex',
  flexFlow: 'wrap',
  gap: 10,
  marginTop: 5,
};

const additionalStyle = {
  width: 'calc(50% - 10px)',
  margin: 0,
  paddingLeft: 0,
  paddingRight: 0,
};

const SliderLabel: React.FC<{ isHeightInFt: boolean; children?: any }> = ({ isHeightInFt, children }) => {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {isHeightInFt ? (
        <>
          <span className="label" style={{ fontSize: 14 }}>{`<5'`}</span>
          {children}
          <span className="label" style={{ float: 'right', fontSize: 14 }}>{`>7'`}</span>
        </>
      ) : (
        <>
          <span className="label" style={{ fontSize: 14 }}>{`< 152cm`}</span>
          {children}
          <span className="label" style={{ float: 'right', fontSize: 14 }}>{`>214cm`}</span>
        </>
      )}
    </div>
  );
};

const CompleteProfileDetailsForm = ({
  formData,
  handleFormData,
  showRequiredProfileDetailLists,
  gender,
  country,
  occupation,
}: ICompleteProfileDetailsFormProps): ReactElement => {
  const [height, setHeight] = useState('0');
  const [isHeightInFt, setIsHeightInFt] = useState(shouldShowHeignInFeet(country));
  const [heightInCm, setHeightInCm] = useState(151);
  const [heightInFt, setHeightInFt] = useState('5');
  const [defaultHeight, setDefaultHeight] = useState<any[]>();

  const filteredBodyType = useFilteredBodyType(gender);

  const onHeightMeasurementChange = (value: boolean) => {
    setIsHeightInFt(value);
  };

  useEffect(() => {
    if (defaultHeight) {
      calculateHeight(defaultHeight[0]);
    }
  }, [defaultHeight, isHeightInFt]);

  const calculateHeight = (value: number): void => {
    setHeightInCm(value);

    const convertHeightInFt = convertCMToInchesHeight(value);
    const validHeight = convertHeightInFt ?? '0';

    if (isHeightInFt) {
      setHeight(validHeight);
    } else {
      setHeight(value ? value.toFixed(0) : '0');
    }

    setHeightInFt(convertHeightInFt);
  };

  const getHeightFormDataValue = () => {
    if (heightInCm > 213) {
      return `> 7'0" (> 214cm)`;
    }

    if (heightInCm < 153) {
      return `< 5'0" (<152cm)`;
    }

    return HEIGHTS_VALUES[heightInFt];
  };

  useEffect(() => {
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    handleFormData({ height: getHeightFormDataValue() });
  }, [heightInCm]);

  // set default height
  useEffect(() => {
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    handleFormData({ height: getHeightFormDataValue() });
  }, []);

  const hasCounterError = formData?.occupation ? formData.occupation?.length > OCCUPATION_MAX_CHARACTER : false;

  const showOccupationCounter = (
    <Counter hasError={hasCounterError}>
      {formData?.occupation?.length || 0}/{OCCUPATION_MAX_CHARACTER}
    </Counter>
  );

  const memoizedBodyType = useMemo(
    () =>
      showRequiredProfileDetailLists.body_type && (
        <div style={{ overflow: 'hidden' }}>
          <JoinRadioButtonFormGroup label="Body Type" isRequired subLabel="(select one)" inlineSubLabel={true}>
            <JoinOptionButtonGroup
              lists={filteredBodyType}
              value={formData.body_type}
              onChangeHandler={item => {
                if (formData.body_type === item.value) {
                  return handleFormData({
                    body_type: null,
                  });
                }
                handleFormData({ body_type: item.value });
              }}
              customButtonGroupStyle={customButtonGroupStyle}
              additionalStyle={additionalStyle}
              isCompletionProfileForm
            />
          </JoinRadioButtonFormGroup>
        </div>
      ),
    [showRequiredProfileDetailLists.body_type, formData.body_type, filteredBodyType]
  );

  const memoizedHeight = useMemo(
    () =>
      showRequiredProfileDetailLists.height && (
        <div>
          <JoinCheckboxFormGroup customStyle={{ position: 'relative' }} label="Height" isRequired>
            <StyleSlider className="switchSWUI">
              <Switch onText="in" offText="cm" checked={isHeightInFt} onChange={onHeightMeasurementChange} />
            </StyleSlider>
            <StyleSlider>
              <Slider
                min={152}
                max={214}
                defaultValue={[]}
                onHandleChange={height => {
                  setDefaultHeight(height);
                }}
              />
            </StyleSlider>
            <SliderLabel isHeightInFt={isHeightInFt}>
              <SliderValue style={{ left: isHeightInFt ? '48%' : '44%' }}>
                {
                  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                  height === 0 ? '' : !isHeightInFt && height
                }
                {isHeightInFt ? cmToFeetInches(heightInCm) : 'cm'}
              </SliderValue>
            </SliderLabel>
          </JoinCheckboxFormGroup>
          <DashlineSeparator />
        </div>
      ),
    [showRequiredProfileDetailLists.height, height, isHeightInFt]
  );

  const memoizedChildren = useMemo(
    () =>
      showRequiredProfileDetailLists.children && (
        <div>
          <JoinRadioButtonFormGroup label="Do You Have Any Children?" isRequired>
            <JoinOptionButtonGroup
              lists={YES_NO_OPTIONS(false, true)}
              value={formData.children}
              onChangeHandler={item => {
                if (formData.children === item.value) {
                  return handleFormData({
                    children: null,
                  });
                }
                handleFormData({ children: item.value });
              }}
              isCompletionProfileForm
            />
          </JoinRadioButtonFormGroup>
          <DashlineSeparator />
        </div>
      ),
    [showRequiredProfileDetailLists.children, formData.children]
  );

  const memoizedSmoking = useMemo(
    () =>
      showRequiredProfileDetailLists.smoking && (
        <div>
          <JoinRadioButtonFormGroup label="Do You Smoke?" isRequired>
            <JoinOptionButtonGroup
              lists={YES_NO_OPTIONS(true, true)}
              value={formData.smoking}
              onChangeHandler={item => {
                if (formData.smoking === item.value) {
                  return handleFormData({
                    smoking: null,
                  });
                }
                handleFormData({ smoking: item.value });
              }}
              isCompletionProfileForm
            />
          </JoinRadioButtonFormGroup>
          <DashlineSeparator />
        </div>
      ),
    [showRequiredProfileDetailLists.smoking, formData.smoking]
  );

  const memoizedDrinking = useMemo(
    () =>
      showRequiredProfileDetailLists.drinking && (
        <div>
          <JoinRadioButtonFormGroup label="Do You Drink?" isRequired>
            <JoinOptionButtonGroup
              lists={YES_NO_OPTIONS(true, true)}
              value={formData.drinking}
              onChangeHandler={item => {
                if (formData.drinking === item.value) {
                  return handleFormData({
                    drinking: null,
                  });
                }
                handleFormData({ drinking: item.value });
              }}
              isCompletionProfileForm
            />
          </JoinRadioButtonFormGroup>
          <DashlineSeparator />
        </div>
      ),
    [showRequiredProfileDetailLists.drinking, formData.drinking]
  );

  const memoizedEducation = useMemo(
    () =>
      showRequiredProfileDetailLists.education && (
        <div>
          <JoinRadioButtonFormGroup label="Education" isRequired subLabel="(select one)" inlineSubLabel={true}>
            <JoinOptionButtonGroup
              lists={EDUCATION_OPTIONS}
              value={formData.education}
              onChangeHandler={item => {
                if (formData.education === item.value) {
                  return handleFormData({
                    education: null,
                  });
                }
                handleFormData({ education: item.value });
                handleProfileCompletionVysionEvent(
                  {
                    action: VYSION_ACTIONS.CLICK,
                    event: ProfileCompletionClickVysionEventsEnum.CLICK_FINISH_PROFILE_EDUCATION,
                  },
                  location.pathname
                );
              }}
              customButtonGroupStyle={customButtonGroupStyle}
              additionalStyle={additionalStyle}
              isCompletionProfileForm
            />
          </JoinRadioButtonFormGroup>
          <DashlineSeparator />
        </div>
      ),
    [showRequiredProfileDetailLists.education, formData.education]
  );

  const memoizedOccupation = useMemo(
    () =>
      showRequiredProfileDetailLists.occupation && (
        <div>
          <JoinCheckboxFormGroup
            label="Occupation"
            extraLabelInfo={showOccupationCounter}
            isRequired={occupation?.length > OCCUPATION_MAX_CHARACTER}
          >
            <TextInput
              id="occupation"
              value={formData.occupation ?? ''}
              customStyle={{ width: '100%', paddingBottom: 0, fontSize: 14 }}
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                handleFormData({ occupation: event.target.value });
              }}
              joinForm={true}
              placeholder="Type your Occupation here..."
              status={hasCounterError ? 'error' : 'default'}
            />
          </JoinCheckboxFormGroup>
          <DashlineSeparator />
        </div>
      ),
    [showRequiredProfileDetailLists.occupation, formData.occupation, showOccupationCounter, occupation]
  );

  const memoizedIncome = useMemo(
    () =>
      showRequiredProfileDetailLists.income && (
        <div>
          <JoinRadioButtonFormGroup label="Annual Income" isRequired subLabel="(select one)" inlineSubLabel={true}>
            <JoinOptionButtonGroup
              lists={ANNUAL_INCOME_OPTIONS.filter(item => item.value > 0)}
              value={formData.income}
              onChangeHandler={item => {
                if (formData.income === item.value) {
                  return handleFormData({
                    income: null,
                  });
                }
                handleFormData({ income: item.value });
                handleProfileCompletionVysionEvent(
                  {
                    action: VYSION_ACTIONS.CLICK,
                    event: ProfileCompletionClickVysionEventsEnum.CLICK_FINISH_PROFILE_ANNUAL_INCOME,
                  },
                  location.pathname
                );
              }}
              customButtonGroupStyle={customButtonGroupStyle}
              additionalStyle={additionalStyle}
              isCompletionProfileForm
            />
          </JoinRadioButtonFormGroup>
          <DashlineSeparator />
        </div>
      ),
    [showRequiredProfileDetailLists.income, formData.income]
  );

  const memoizedNetWorth = useMemo(
    () =>
      showRequiredProfileDetailLists.net_worth && (
        <div>
          <JoinRadioButtonFormGroup label="Net Worth" isRequired subLabel="(select one)" inlineSubLabel={true}>
            <JoinOptionButtonGroup
              name="net_worth"
              lists={NET_WORTH_OPTIONS.filter(item => item.value > 0)}
              value={formData.net_worth}
              onChangeHandler={item => {
                if (formData.net_worth === item.value) {
                  return handleFormData({
                    net_worth: null,
                  });
                }
                handleFormData({ net_worth: item.value });
              }}
              customButtonGroupStyle={customButtonGroupStyle}
              additionalStyle={additionalStyle}
              isCompletionProfileForm
            />
          </JoinRadioButtonFormGroup>
          <DashlineSeparator />
        </div>
      ),
    [showRequiredProfileDetailLists.net_worth, formData.net_worth]
  );

  return (
    <div style={{ overflow: 'hidden' }}>
      {showRequiredProfileDetailLists.username && (
        <div>
          <UsernameInputV2
            inputLabel="First Name / Nickname"
            onChange={e => {
              handleFormData({ username: e.target.value });
            }}
            value={formData.username}
            customStyle={{
              marginRight: 0,
              fontSize: 15,
              fontWeight: '600 !important',
              paddingBottom: 0,
            }}
            customStyleLabel={{ fontWeight: 600, marginRight: 8 }}
            placeholder="First names exhibit authenticity"
            joinForm
            isRequired
            showInfoIcon
          />
          <DashlineSeparator />
        </div>
      )}

      {memoizedBodyType}
      {memoizedHeight}
      {memoizedChildren}
      {memoizedSmoking}
      {memoizedDrinking}
      {memoizedEducation}
      {memoizedOccupation}
      {memoizedIncome}
      {memoizedNetWorth}
    </div>
  );
};

export default memo(CompleteProfileDetailsForm);
